import { Button, cn, Flex, Icon } from '@kognitos/lattice';

import type { TRainToolCallStatus } from './RainToolCallCard';

export interface IRainTestRunCardProps {
  label: string;
  status: TRainToolCallStatus;
  isViewingRun?: boolean;
  onClick: () => void;
}

/**
 * Whole-row trigger for a `test_automation`/`replay_run` tool call. The
 * row's only purpose is to focus that run's status onto the manifest step
 * view, so the entire row is the click target — one click, not the previous
 * collapsible-header-plus-separate-button pattern. Visually matches the
 * other system-status cards in the same chat feed (RainSleepCard,
 * RainCompactionCard: flat `bg-muted/40` box, bare inline icon) rather than
 * the boxed icon-badge used for connection rows elsewhere in the app.
 */
export function RainTestRunCard({
  label,
  status,
  isViewingRun = false,
  onClick,
}: IRainTestRunCardProps) {
  const iconType =
    status === 'running' ? 'Loader2' : status === 'error' ? 'X' : 'Check';
  const iconColorClassName =
    status === 'running'
      ? 'text-muted-foreground'
      : status === 'error'
        ? 'text-destructive'
        : 'text-success';

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      aria-label={label}
      aria-pressed={isViewingRun}
      data-testid="rain-test-run-card"
      data-viewing={isViewingRun ? 'true' : 'false'}
      className={cn(
        'h-auto w-full justify-start rounded-md border p-3 text-left font-normal',
        isViewingRun
          ? 'border-primary bg-primary/5 not-disabled:hover:bg-primary/5'
          : 'border-border bg-muted/40 not-disabled:hover:bg-muted/60',
      )}
    >
      <Flex
        direction="row"
        align="center"
        justify="between"
        gap={2}
        className="w-full"
      >
        <Flex direction="row" align="center" gap={2} className="min-w-0">
          <Icon
            type={iconType}
            size="sm"
            className={cn(
              'shrink-0',
              iconColorClassName,
              status === 'running' && 'animate-spin',
            )}
          />
          <span className="truncate text-sm font-medium text-foreground">
            {label}
          </span>
        </Flex>
        <Icon
          type={isViewingRun ? 'Eye' : 'ChevronRight'}
          size="sm"
          className={cn(
            'shrink-0',
            isViewingRun ? 'text-primary' : 'text-muted-foreground',
          )}
        />
      </Flex>
    </Button>
  );
}
